import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Layout } from './components/Layout';
import { PageLoading } from './components/LoadingSpinner';
import { Toaster } from 'sonner';
import { isNativeApp, loggedOutHome } from './lib/platform';

// Landing Page
import { LandingPage } from './pages/LandingPage';

// Auth Pages
import { LoginPage } from './pages/auth/LoginPage';
import { RegisterPage } from './pages/auth/RegisterPage';

// User Pages
import { HomePage } from './pages/user/HomePage';
import { QuizPage } from './pages/user/QuizPage';
import { ResultsPage } from './pages/user/ResultsPage';
import { RankingsPage } from './pages/user/RankingsPage';
import { GroupsPage } from './pages/user/GroupsPage';
import { ProfilePage } from './pages/user/ProfilePage';
import { RewardsPage } from './pages/user/RewardsPage';
import { DashboardPage } from './pages/user/DashboardPage';
import { LearningPathPage } from './pages/user/LearningPathPage';
import { JoinGroupPage } from './pages/user/JoinGroupPage';
import { PracticePage } from './pages/user/PracticePage';

// Admin Pages
import { AdminLayout } from './components/AdminLayout';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { AdminTopicsPage } from './pages/admin/AdminTopicsPage';
import { AdminQuestionsPage } from './pages/admin/AdminQuestionsPage';
import { AdminAdsPage } from './pages/admin/AdminAdsPage';
import { AdminNotificationsPage } from './pages/admin/AdminNotificationsPage';
import { AdminImageQuizPage } from './pages/admin/AdminImageQuizPage';
import { AdminUsersPage } from './pages/admin/AdminUsersPage';
import { AdminReportsPage } from './pages/admin/AdminReportsPage';
import { AdminSettingsPage } from './pages/admin/AdminSettingsPage';

// Legal Pages
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import RewardsRulesPage from './pages/RewardsRulesPage';
import DeleteAccountPage from './pages/DeleteAccountPage';
import { AdSenseManager } from './components/GoogleAds';

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoading />;
  }

  return user ? children : <Navigate to={loggedOutHome()} replace />;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoading />;
  }

  return !user ? children : <Navigate to="/home" />;
}

// "/" and "/welcome": the website shows the landing page; the iOS/Android app goes straight to login
// (no "download the app" page inside the app).
function LandingRoute() {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoading />;
  }
  if (user) return <Navigate to="/home" replace />;
  if (isNativeApp()) return <Navigate to="/login" replace />;
  return <LandingPage />;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoading />;
  }
  if (!user) return <Navigate to={loggedOutHome()} replace />;
  if (user.role !== 'admin') return <Navigate to="/home" />;

  return <AdminLayout>{children}</AdminLayout>;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster 
          position="top-center" 
          richColors 
          theme="dark"
          toastOptions={{
            style: {
              background: '#1e293b',
              color: '#f1f5f9',
              border: '1px solid #334155',
            },
          }}
        />
        <AdSenseManager />
        <Routes>
          {/* Landing Page - First thing visitors see */}
          <Route path="/" element={<LandingRoute />} />
          <Route path="/welcome" element={<LandingRoute />} />
          
          {/* Auth Routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            }
          />

          {/* Private Routes - Logged in users */}
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Layout>
                  <DashboardPage />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/learning"
            element={
              <PrivateRoute>
                <Layout>
                  <LearningPathPage />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/home"
            element={
              <PrivateRoute>
                <Layout>
                  <HomePage />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/quiz/:quizIndex"
            element={
              <PrivateRoute>
                <Layout>
                  <QuizPage />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/results/:quizIndex"
            element={
              <PrivateRoute>
                <Layout>
                  <ResultsPage />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/rankings"
            element={
              <PrivateRoute>
                <Layout>
                  <RankingsPage />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/groups"
            element={
              <PrivateRoute>
                <Layout>
                  <GroupsPage />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <Layout>
                  <ProfilePage />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/rewards"
            element={
              <PrivateRoute>
                <Layout>
                  <RewardsPage />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/practice"
            element={
              <PrivateRoute>
                <Layout>
                  <PracticePage />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/admin/topics" element={<AdminRoute><AdminTopicsPage /></AdminRoute>} />
          <Route path="/admin/questions" element={<AdminRoute><AdminQuestionsPage /></AdminRoute>} />
          <Route path="/admin/image-quiz" element={<AdminRoute><AdminImageQuizPage /></AdminRoute>} />
          <Route path="/admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
          <Route path="/admin/reports" element={<AdminRoute><AdminReportsPage /></AdminRoute>} />
          <Route path="/admin/ads" element={<AdminRoute><AdminAdsPage /></AdminRoute>} />
          <Route path="/admin/notifications" element={<AdminRoute><AdminNotificationsPage /></AdminRoute>} />
          <Route path="/admin/settings" element={<AdminRoute><AdminSettingsPage /></AdminRoute>} />

          {/* Public Legal Pages - No auth required */}
          {/* Group invite links (works logged-in or logged-out) */}
          <Route path="/groups/join/:code" element={<JoinGroupPage />} />

          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/rewards-rules" element={<RewardsRulesPage />} />
          <Route path="/official-rules" element={<RewardsRulesPage />} />
          <Route path="/delete-account" element={<DeleteAccountPage />} />
          <Route path="/account-deletion" element={<DeleteAccountPage />} />

          {/* Catch all - redirect to landing or home based on auth */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
